import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsAllowed, CONSENT_REGIONS, useConsent } from '@/lib/consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Removes Google Analytics cookies after a visitor declines. */
function clearAnalyticsCookies() {
  const host = window.location.hostname;
  const domains = ['', host, `.${host.replace(/^www\./, '')}`];
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_'))
    .forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
      });
    });
}

function loadGtag(id: string) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the Arguments object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  // Consent Mode defaults: opt-in regions start denied, everywhere else
  // granted. Ad storage stays denied everywhere because HEXDI runs no ads.
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    region: CONSENT_REGIONS,
  });
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  // Page views are sent manually below because React Router changes
  // routes without a page load.
  window.gtag('config', id, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
}

/**
 * Loads Google Analytics 4 and records a page view on every route change.
 *
 * Mounted only inside `MarketingLayout`, so the logged-in app (dashboard,
 * reports, settings, admin) and the auth pages never send page views.
 * Respects the visitor's consent choice (see `@/lib/consent`).
 *
 * Disabled unless `VITE_GA_ID` is set at build time, so local and staging
 * builds stay untracked.
 */
export function GoogleAnalytics() {
  const { pathname, search } = useLocation();
  const { choice } = useConsent();
  const allowed = analyticsAllowed(choice);

  useEffect(() => {
    const id = import.meta.env.VITE_GA_ID;
    if (!id) return;
    if (!allowed) {
      if (window.gtag) {
        window.gtag('consent', 'update', { analytics_storage: 'denied' });
        clearAnalyticsCookies();
      }
      return;
    }
    if (!window.gtag) loadGtag(id);
    if (choice === 'granted') window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
    // Wait a frame so react-helmet-async has set the new page's <title>.
    const frame = requestAnimationFrame(() => {
      window.gtag?.('event', 'page_view', {
        page_location: window.location.href,
        page_title: document.title,
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, search, allowed, choice]);

  return null;
}
